import { Check } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Button } from "@/components/ui/button";

function Price({ pkg }) {
  return (
    <div className="mt-6 flex items-baseline gap-3">
      <span className="font-display text-4xl md:text-5xl text-[#F5F5F5]">
        ${pkg.price.toLocaleString()}
      </span>
      <span className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3]">
        from · {pkg.hours}
      </span>
    </div>
  );
}

function Feature({ children }) {
  return (
    <li className="flex items-start gap-3 text-sm text-[#D4D4D4] font-light">
      <Check className="h-4 w-4 mt-0.5 shrink-0 text-[#D98B48]" />
      {children}
    </li>
  );
}

function SelectButton({ pkgId, onSelect, dark }) {
  return (
    <Button
      data-testid={`package-select-${pkgId}`}
      onClick={() => onSelect(pkgId)}
      className={
        dark
          ? "rounded-sm bg-[#D98B48] hover:bg-[#F0A562] text-[#090909] font-medium w-full py-6"
          : "rounded-sm border border-white/20 bg-transparent text-[#F5F5F5] hover:bg-white/10 hover:text-[#F5F5F5] w-full py-6 transition-colors"
      }
    >
      Select — reserve with deposit
    </Button>
  );
}

export default function Packages({ packages, onSelect }) {
  const byId = (id) => packages.find((p) => p.id === id);
  const featured = byId("the_full_pour");
  const well = byId("the_well");
  const topShelf = byId("top_shelf");
  const bartender = byId("just_the_bartender");

  return (
    <section id="packages" className="py-24 md:py-32 bg-[#0C0C0C] border-y border-white/10">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <p className="eyebrow mb-5">Packages</p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight text-[#F5F5F5]">
              Four ways to <em className="italic text-[#D98B48]">pour</em>.
            </h2>
          </div>
          <p className="text-sm text-[#A3A3A3] max-w-xs font-light">
            Deposits reserve your date and come off the final total. Balance due
            on event day.
          </p>
        </Reveal>

        <div className="grid lg:grid-cols-5 gap-5 auto-rows-auto">
          {featured && (
            <Reveal className="lg:col-span-3 lg:row-span-2" delay={0}>
              <div className="relative overflow-hidden border border-white/10 h-full min-h-[480px] flex" data-testid="package-card-the_full_pour">
                <img
                  src="https://images.unsplash.com/photo-1777063660001-9953ce697faf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBiYXIlMjBzZXR1cHxlbnwwfHx8fDE3OTEyMTYzNjd8MA&ixlib=rb-4.1.0&q=85"
                  alt="Luxury back bar with bottles"
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-[#090909]/80" />
                <div className="relative p-8 md:p-12 flex flex-col">
                  <span className="self-start text-[10px] uppercase tracking-[0.28em] bg-[#D98B48] text-[#090909] px-3 py-1.5 rounded-sm font-medium">
                    Most requested
                  </span>
                  <h3 className="mt-6 font-display text-5xl md:text-6xl text-[#F5F5F5]">{featured.name}</h3>
                  <p className="mt-2 italic font-display text-xl text-[#C5A059]">{featured.tagline}</p>
                  <Price pkg={featured} />
                  <p className="mt-2 text-xs uppercase tracking-[0.2em] text-[#A3A3A3]">
                    ${featured.deposit} deposit · {featured.guest_range}
                  </p>
                  <ul className="mt-8 space-y-3 flex-1">
                    {featured.features.map((f) => (
                      <Feature key={f}>{f}</Feature>
                    ))}
                  </ul>
                  <div className="mt-10">
                    <SelectButton pkgId={featured.id} onSelect={onSelect} dark />
                  </div>
                </div>
              </div>
            </Reveal>
          )}

          {well && (
            <Reveal className="lg:col-span-2" delay={0.1}>
              <div className="border border-white/10 bg-[#141414] p-8 md:p-10 h-full flex flex-col" data-testid="package-card-the_well">
                <h3 className="font-display text-4xl text-[#F5F5F5]">{well.name}</h3>
                <p className="mt-1 italic font-display text-lg text-[#C5A059]">{well.tagline}</p>
                <Price pkg={well} />
                <p className="mt-2 text-xs uppercase tracking-[0.2em] text-[#A3A3A3]">
                  ${well.deposit} deposit · {well.guest_range}
                </p>
                <ul className="mt-7 space-y-3 flex-1">
                  {well.features.map((f) => (
                    <Feature key={f}>{f}</Feature>
                  ))}
                </ul>
                <div className="mt-8">
                  <SelectButton pkgId={well.id} onSelect={onSelect} />
                </div>
              </div>
            </Reveal>
          )}

          {topShelf && (
            <Reveal className="lg:col-span-2" delay={0.2}>
              <div className="border border-white/10 bg-[#141414] p-8 md:p-10 h-full flex flex-col" data-testid="package-card-top_shelf">
                <h3 className="font-display text-4xl text-[#F5F5F5]">{topShelf.name}</h3>
                <p className="mt-1 italic font-display text-lg text-[#C5A059]">{topShelf.tagline}</p>
                <Price pkg={topShelf} />
                <p className="mt-2 text-xs uppercase tracking-[0.2em] text-[#A3A3A3]">
                  ${topShelf.deposit} deposit · {topShelf.guest_range}
                </p>
                <ul className="mt-7 space-y-3 flex-1">
                  {topShelf.features.map((f) => (
                    <Feature key={f}>{f}</Feature>
                  ))}
                </ul>
                <div className="mt-8">
                  <SelectButton pkgId={topShelf.id} onSelect={onSelect} />
                </div>
              </div>
            </Reveal>
          )}

          {bartender && (
            <Reveal className="lg:col-span-5" delay={0.15}>
              <div className="border border-white/10 bg-[#141414] p-8 md:p-10 flex flex-col lg:flex-row lg:items-center gap-8" data-testid="package-card-just_the_bartender">
                <div className="lg:w-72 shrink-0">
                  <h3 className="font-display text-4xl text-[#F5F5F5]">{bartender.name}</h3>
                  <p className="mt-1 italic font-display text-lg text-[#C5A059]">{bartender.tagline}</p>
                  <Price pkg={bartender} />
                  <p className="mt-2 text-xs uppercase tracking-[0.2em] text-[#A3A3A3]">
                    ${bartender.deposit} deposit · {bartender.guest_range}
                  </p>
                </div>
                <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3 flex-1">
                  {bartender.features.map((f) => (
                    <Feature key={f}>{f}</Feature>
                  ))}
                </ul>
                <div className="lg:w-64 shrink-0">
                  <SelectButton pkgId={bartender.id} onSelect={onSelect} />
                </div>
              </div>
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
}
